import * as React from 'react';
import * as api from 'app/shared/api';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Table,
  Row,
  Col,
  Input,
  Select,
  Modal,
  Button,
  Space,
} from 'antd';
import { PlusOutlined, EditOutlined, LockOutlined } from '@ant-design/icons';
import {
  FilterValueType,
  Product,
  ProductFilter,
  ProductKey
} from 'app/shared/model/ProductBase';
import {
  filterData,
  getCourseProductById,
  listCourseProductsData,
  resetCourseModal,
  setFormErrors
} from 'app/pages/Products/actions';
import { getFilteredData, getCourseProductsData } from '../selectors';
import { CourseProduct } from 'app/shared/model/Course';
import DateHelper from 'app/helpers/dateHelper';
import filterHelper from 'app/helpers/filterHelper';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import AddEditCourseForm from './AddEditCourseForm';
import { useNavigate } from 'react-router-dom';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';

import { DivisionId } from 'app/shared/division/getDivisionId';
interface StateProps {
  filteredData?: Product[];
  courseProducts?: CourseProduct[];
}

interface DispatchProps {
  onCancel: () => void;
  onFilterData: (filteredData: Product[] | undefined) => void;
  onCourseData: (courseProducts: CourseProduct[] | undefined) => void;
  onSelectedCourseData: (
    courseProduct: CourseProduct | undefined | void
  ) => void;
}

type InternalProps = StateProps & DispatchProps;

function CoursesGrid(props: InternalProps) {
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState<ProductFilter[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [searchFilteredData, setSearchFilteredData] = React.useState<CourseProduct[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();

  const fullList = props.courseProducts ?? [];

  const publishFilteredData = React.useCallback((
    nextFilters = filters,
    nextShowFilters = showFilters,
    nextStatus = statusFilter,
    nextSearchActive = isSearchActive,
    nextSearchData = searchFilteredData,
  ) => {
    props.onFilterData(buildGridFilteredList(fullList, {
      showFilters: nextShowFilters,
      filters: nextFilters,
      isSearchActive: nextSearchActive,
      searchFilteredData: nextSearchData,
      statusFilter: nextStatus,
    }));
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, fullList, props.onFilterData]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalCourseId, setModalCourseId] = React.useState<number | null>(null);
  const navigate = useNavigate();

  const refreshGridEvent = async () => {
    const list = await api.getCourseProducts(DivisionId());
    props.onCourseData(list);

    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.CourseEventPublished], refreshGridEvent, DivisionId());

  React.useEffect(() => {
    // Initial Load
    if (props.courseProducts == null) {
      api.getCourseProducts(DivisionId()).then(props.onCourseData);
    }
  }, []/*[props.courseProducts]*/);

  const getCourseProduct = (id: number) => {
    api.getCourseById(DivisionId(), id.toString()).then(cp => {
      props.onSelectedCourseData(cp);
      setModalCourseId(id);
      setModalOpen(true);
    });
  };

  useAutoOpenProductModal('Courses', getCourseProduct);

    // Filter helpers
    const getUniqueLanguageFilter = (list: CourseProduct[]) => {
        const data = list.map(d => ({ id: d.languageText, name: d.languageText }));
        const unique = data.filter((v, i, a) => v.id && a.findIndex(x => x.id === v.id) === i);
        return unique.map(v => ({ label: v.name, value: v.id }));
    };

    const getUniqueIntensityFilter = (list: CourseProduct[]) => {
      const data = list.map(i => ({ id: i.intensityId, name: i.intensityText }));
      const unique = data.filter((v, i, a) => v.id && a.findIndex(x => x.id === v.id) === i);
      return unique.map(v => ({ label: v.name, value: v.id }));
    };

    const getUniqueUnitTypeFilter = (list: CourseProduct[]) => {
      const texts = new Set<string>();
      list.forEach((d) => {
        const t = (d.unitTypeText ?? '').trim();
        if (t) texts.add(t);
      });
      return Array.from(texts)
        .sort((a, b) => a.localeCompare(b))
        .map((t) => ({ label: t, value: t }));
    };

  const handleClearFilters = () => {
    setFilters([]);
    setCurrentPage(1);
    publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
  };

  const renderFilterRow = () =>
    showFilters && (
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col>
          <Input
            placeholder="Search..."
            value={filterHelper.getFieldValue(filters, 'name', '')}
            onChange={e => handleFilterChange(e.target.value, 'name')}
            allowClear
            style={{ width: 140 }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            placeholder="Language"
            value={filterHelper.getFieldValue(filters, 'languageText')}
            onChange={vals => handleFilterChange(vals, 'languageText')}
            options={getUniqueLanguageFilter(fullList)}
            style={{ width: 140 }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            placeholder="Intensity"
            value={filterHelper.getFieldValue(filters, 'intensityId')}
            onChange={vals => handleFilterChange(vals, 'intensityId')}
            options={getUniqueIntensityFilter(fullList)}
            style={{ width: 140 }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            placeholder="Unit Type"
            value={filterHelper.getFieldValue(filters, 'unitTypeText')}
            onChange={(vals) => handleFilterChange(vals, 'unitTypeText')}
            options={getUniqueUnitTypeFilter(fullList)}
            style={{ width: 160 }}
          />
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          <FilterResetLink onClick={handleClearFilters} />
        </Col>
      </Row>
    );

 const openNewCourse = () => {
    props.onCancel();

    const blankCourse: CourseProduct = {
      id: 0,
      name: '',
      languageId: 0,
      languageText: '',
      intensityId: 0,
      intensityText: '',
      division: 0,
      divisionText: '',
      closureDate: null,
      productCategoryText: '',
      accountCategoryText: '',
      lastModifiedDate: new Date(),
      lastUpdatedBy: '',
      entityUpdate: false,
      active: true,
      excludedBookingSystemIds: [],
        minimumAge: null,
        minimumWeeks: null,
      unitTypeId: undefined,
      unitTypeText: undefined,
    };

    props.onSelectedCourseData(blankCourse);
    setModalCourseId(null);
    setModalOpen(true);
  };

  const handleFilterChange = (
    value: any,
    field: keyof ProductKey,
    valueType: FilterValueType = FilterValueType.primaryValue
  ) => {
    const safeValue = value ?? [];
    const newFilters = filterHelper.getFilters(filters ?? [], safeValue, field, valueType);
    setFilters(newFilters);
    publishFilteredData(newFilters);
    setCurrentPage(1);
  };

  const filtered = props.filteredData ?? fullList;
  const sortedFiltered = React.useMemo(() => {
    return sortProductsWithStatus(filtered as CourseProduct[], sortKey, sortOrder, statusFilter);
  }, [filtered, sortKey, sortOrder, statusFilter]);
  const pageData = TablePagination.dataPaging(
    sortedFiltered as CourseProduct[],
    currentPage,
    itemsPerPage
  ) as CourseProduct[];
  const totalItems = sortedFiltered.length;

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number, rec: CourseProduct) => (
        <>
          {id}
          {rec.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
        </>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: CourseProduct) =>
        record.entityUpdate ? (
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
        ) : (
          <a
            href="#0"
            onClick={e => {
              e.preventDefault();
              getCourseProduct(Number(record.id));
            }}
          >
            {text}
          </a>
        )
    },
    { title: 'Language', dataIndex: 'languageText', key: 'languageText', width: 140 },
    {
      title: (
        <span>
          Intensity{' '}
          <EditOutlined
            style={{ cursor: 'pointer', color: '#d9d9d9' }}
            onClick={() => navigate('/course-intensity-manager')}
            title="Manage Intensity"
          />
        </span>
      ),
      dataIndex: 'intensityText',
      key: 'intensityText',
      width: 120
    },
      {
          title: 'Min Weeks',
          dataIndex: 'minimumWeeks',
          key: 'minimumWeeks',
          width: 110,
          render: (v: number | null | undefined) => (v ?? '-'),
      },
      {
          title: 'Min Age',
          dataIndex: 'minimumAge',
          key: 'minimumAge',
          width: 110,
          render: (v: number | null | undefined) => (v ?? '-'),
      },
      {
        title: 'Unit Type',
        dataIndex: 'unitTypeText',
        key: 'unitTypeText',
        width: 120,
        render: (_: string | undefined, r: CourseProduct) => (r.unitTypeText ?? '').trim() || '-',
      },
    {
      title: 'Last Modified',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 160,
      ...getColumnSortProps('lastModifiedDate'),
      render: (d: Date) => DateHelper.ToDateString(d)
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 150,
      ...getColumnSortProps('lastUpdatedBy'),
    },
    {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        width: 100,
        render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={fullList}
                onSearch={(data, searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(data as CourseProduct[]);
                  publishFilteredData(filters, showFilters, statusFilter, searchActive, data as CourseProduct[]);
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
              }}
            />
          </div>
        </Col>
        <Col flex={3} style={{ textAlign: 'right' }}>
          <Space>
            <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(pageData) && pageData.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent} />
            <TableActionButtons
                showFilters={showFilters}
                dataList={filtered}
                onShowFiltersClick={() => {
                    const nextShowFilters = !showFilters;
                    setShowFilters(nextShowFilters);
                    if (nextShowFilters) {
                        setFilters([]);
                        publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                    } else {
                        publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                    }
                    setCurrentPage(1);
                } }
                canFilter
                canExport
                csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Courses_Export`}
                canEdit={false}
                canAdd={false}
                />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNewCourse}
            >
              Add New Course
            </Button>
          </Space>
        </Col>
      </Row>

      {renderFilterRow()}

      <Table
        columns={columns}
        dataSource={pageData}
        rowKey="id"
        pagination={false}
        bordered
        size="middle"
        style={{ marginBottom: 12 }}
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
      />

      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(value, page) => {
          setItemsPerPage(value);
          setCurrentPage(page);
        }}
        onPageChange={(page) => setCurrentPage(page)}
        showItemsPerPage
      />

      <Modal
        title={modalCourseId ? 'Update Course' : 'Add Course'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          props.onCancel();
        }}
        footer={null}
        width={980}
        style={{ top: 50 }}
        destroyOnHidden
      >
        <AddEditCourseForm
          editMode={!!modalCourseId}
          onSuccess={() => {
            setModalOpen(false);
            props.onCancel();
          }}
        />
      </Modal>
    </div>
  );
}

const mapStateToProps = (state: any): StateProps => ({
  filteredData: getFilteredData(state),
  courseProducts: getCourseProductsData(state)
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  onFilterData: (filteredData) => dispatch(filterData(filteredData)),
  onCourseData: cp => dispatch(listCourseProductsData(cp ?? [])),
  onSelectedCourseData: cp => {
    if (cp) {
      dispatch(getCourseProductById(cp));
    }
  },
  onCancel: () => {
    dispatch(setFormErrors([]));
    dispatch(resetCourseModal());
    // api.getCourseProducts(DivisionId()).then(list => {
    //   dispatch(resetCourseModal());
    //   dispatch(listCourseProductsData(list));
    //   dispatch(filterData(list));
    // });
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CoursesGrid);